import React, { useState } from "react";

import { InfoTooltip, SortableList, decisionIdsOf } from "../BuilderUtils";
import { defaultSelection, isPoolPage } from "../measurePool";
import PoolSelectionInspector from "./PoolSelectionInspector";
import { findDecision } from "./common";

/**
 * LES MESURES PROPOSÉES SUR LA PAGE — de deux façons.
 *
 * DÉTERMINISTE : la page porte la liste de ses mesures, dans l'ordre où elles
 * s'afficheront. C'est le cas de toutes les pages écrites jusqu'ici, et le
 * défaut d'une page neuve.
 *
 * PROBABILISTE : la page porte des RÈGLES, et pioche à chaque partie dans le
 * réservoir de l'atelier les mesures qui n'ont pas encore été prises. Deux
 * séances ne montrent alors plus la même grille — ce qui se paie : l'animateur
 * ne peut plus préparer son débrief mesure par mesure, et une page de contexte
 * écrite pour commenter une mesure précise tombe à côté quand elle n'est pas
 * tirée. Les mesures épinglées sont la réponse à ça.
 *
 * LA BASCULE NE DÉTRUIT RIEN : la liste figée reste dans le document sous le
 * mode « tirage », et revenir en arrière la retrouve intacte. C'est ce qui rend
 * l'essai sans risque.
 */
export default function DecisionsInspector(props) {
  const { page, onPatchContent } = props;
  const isPool = isPoolPage(page);

  const setMode = (mode) => {
    if (mode === "pool") onPatchContent({ selection: defaultSelection() });
    // Le mode figé est l'ABSENCE de réglage de tirage, pas un `mode: "fixed"` :
    // c'est ce qui garde les 200 pages existantes sur exactement le même
    // chemin, sans migration.
    else onPatchContent({ selection: null });
  };

  return (
    <div>
      <div className="inspector-subtabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={!isPool}
          className={`inspector-subtab${!isPool ? " is-active" : ""}`}
          onClick={() => setMode("fixed")}
        >Liste figée</button>
        <button
          type="button"
          role="tab"
          aria-selected={isPool}
          className={`inspector-subtab${isPool ? " is-active" : ""}`}
          onClick={() => setMode("pool")}
        >Tirage dans le réservoir</button>
      </div>

      {isPool ? <PoolSelectionInspector {...props} /> : <FixedDecisionsList {...props} />}
    </div>
  );
}

/**
 * LA LISTE FIGÉE.
 *
 * L'ordre de cette liste est l'ordre d'affichage dans la grille : c'est
 * pourquoi elle se réordonne au glisser, et pas seulement d'un cran à la fois.
 *
 * DUPLIQUER PLUTÔT QUE MODIFIER. Une décision est partagée entre les pages qui
 * la référencent : la retoucher pour un atelier la retouche pour tous. Le
 * bouton ⧉ en fait une copie indépendante et rebranche CETTE page dessus —
 * c'est la seule manière d'obtenir une variante locale sans effet de bord.
 */
function FixedDecisionsList({
  page, onPatchContent, ctx, onEditDecision, onCreateDecision, onDuplicateDecision,
  pickerOpen, onPickerOpenChange,
}) {
  // Le sélecteur s'ouvre d'ici (bouton « + Ajouter ») ou de l'extérieur : la
  // case « + » du canevas ouvre l'onglet ET le sélecteur, pour que le geste
  // fait sur le rendu aboutisse au même endroit. Il est donc contrôlable, sans
  // obliger l'appelant à s'en occuper.
  const [localPicker, setLocalPicker] = useState(false);
  const showPicker = onPickerOpenChange ? !!pickerOpen : localPicker;
  const setShowPicker = onPickerOpenChange || setLocalPicker;
  const [search, setSearch] = useState("");

  const ids = decisionIdsOf(page);
  const setIds = (next) => onPatchContent({ list_decisions: next });

  const addDecision = (decId) => {
    setIds([...ids, decId]);
    setShowPicker(false);
    setSearch("");
  };
  const removeDecision = (idx) => setIds(ids.filter((_, i) => i !== idx));

  const filtered = (ctx.allDecisions || []).filter((d) =>
    d._id.toLowerCase().includes(search.toLowerCase()) ||
    (d.title || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="builder-section-header">
        <h3 className="builder-subsection-title">
          <span className="field-label-row">
            Décisions ({ids.length})
            <InfoTooltip text="Liste des décisions proposées aux participants sur cette page. L'ordre ici est l'ordre d'affichage dans la grille." />
          </span>
        </h3>
        <button className="builder-btn-secondary" onClick={() => setShowPicker(true)}>+ Ajouter</button>
      </div>

      {showPicker && (
        <div className="builder-picker-overlay" onClick={() => setShowPicker(false)}>
          <div className="builder-picker" onClick={(e) => e.stopPropagation()}>
            <div className="builder-picker-header">
              <h3>Choisir une décision</h3>
              <button onClick={() => setShowPicker(false)}>✕</button>
            </div>
            <input
              className="builder-search"
              placeholder="Rechercher par ID ou titre…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <div className="builder-picker-list">
              {filtered.slice(0, 60).map((d) => (
                <div key={d._id} className="builder-picker-item" onClick={() => addDecision(d._id)}>
                  <span className="builder-picker-id">{d._id}</span>
                  <span className="builder-picker-title">{d.title || "—"}</span>
                </div>
              ))}
              {filtered.length === 0 && <div className="builder-empty">Aucune décision trouvée</div>}
            </div>
            <div className="builder-picker-footer">
              <button
                className="builder-btn-secondary"
                onClick={() => { setShowPicker(false); onCreateDecision?.(); }}
              >+ Créer une décision</button>
            </div>
          </div>
        </div>
      )}

      <div className="builder-pile">
        <SortableList
          items={ids.map((decId, idx) => {
            // Une même décision peut figurer deux fois : l'index d'occurrence
            // rend la clé stable sans interdire les doublons.
            const occurrence = ids.slice(0, idx).filter((id) => id === decId).length;
            return { id: `${decId}::${occurrence}`, decId };
          })}
          onReorder={(items) => setIds(items.map((i) => i.decId))}
          renderItem={(item, idx) => {
            const dec = findDecision({ ...ctx, page }, item.decId);
            return (
              <div className="builder-pile-item">
                <span className="builder-pile-index">{idx + 1}</span>
                <div className="builder-pile-info">
                  <span className="builder-pile-id">{item.decId}</span>
                  {dec && <span className="builder-pile-title">{dec.title}</span>}
                  {!dec && <span className="builder-pile-missing">⚠ Décision introuvable</span>}
                </div>
                <div className="builder-pile-actions">
                  {/* Pas de flèches : la poignée de glissement en tête de ligne
                      réordonne déjà, et d'un seul geste plutôt que cran par cran. */}
                  <button className="builder-icon-btn danger" onClick={() => removeDecision(idx)} title="Retirer cette mesure de la page">✕</button>
                  <button
                    className="builder-icon-btn"
                    onClick={() => onEditDecision?.(item.decId, dec?.title)}
                    title="Éditer cette décision"
                  >✏</button>
                  {dec && onDuplicateDecision && (
                    <button
                      className="builder-icon-btn"
                      onClick={() => onDuplicateDecision(idx, item.decId)}
                      title="Dupliquer cette décision pour cette page uniquement (les autres pages gardent l'originale)"
                    >⧉</button>
                  )}
                </div>
              </div>
            );
          }}
        />
        {ids.length === 0 && (
          <div className="builder-empty">Aucune décision. Cliquez sur « + Ajouter ».</div>
        )}
      </div>
    </div>
  );
}
